/* The first-visit tour (D68): whether it is open, and whether this device has
   already been shown it.

   The same shape as `lib/theme` — a module-level value, a set of listeners and
   a hook — because three unrelated places drive it: `Tour` opens it on a first
   visit to Now, Profile replays it, and the dev-only `TourToggle` flips it from
   the corner. None of them is a parent of the others, so a context would mean
   hoisting a provider above the router for one boolean.

   **Per device, in `localStorage`, and that is deliberate.** A guest who signs
   in on a phone and later on a laptop sees it twice, which costs them thirty
   seconds; keeping it on the server would cost a field on `/me` and a write on
   the first-login herd, which is the one moment the API cannot spare. */

import { useEffect, useState } from 'react';

const KEY = 'mv.tour.seen';
const listeners = new Set<() => void>();

let open = false;

const notify = () => listeners.forEach((fn) => fn());

/**
 * Wrapped because storage can throw — Safari's private mode on older iOS, a
 * locked-down browser — and a guest who cannot write a flag must still get a
 * working Now page. The failure mode is seeing the tour again, which is fine.
 */
export function tourSeen(): boolean {
  try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
}

function markSeen(seen: boolean) {
  try {
    if (seen) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch { /* see tourSeen */ }
}

export function isTourOpen() { return open; }

/**
 * Remember it has been shown, **the moment it is shown**.
 *
 * It used to be written only when the tour was closed with Skip, Got it or
 * Escape — so a guest who reloaded, left mid-tour, or simply never pressed a
 * button got it again on every visit to Now. Showing it is what "seen" means:
 * once it has been on screen, it is not offered again on this device, however
 * it was left. Profile's "Show me around again" is the way back.
 */
export function markTourShown() { markSeen(true); }

export function startTour() {
  open = true;
  notify();
}

/** Close it. Also marks it seen, which it normally already is by now. */
export function endTour() {
  open = false;
  markSeen(true);
  notify();
}

/**
 * Open it again from the first card. The caller sends the guest to Now, in
 * either order: `Tour` shows whenever the flag is up *and* the route is Now,
 * so the two do not have to land in the same render.
 */
export function replayTour() {
  markSeen(false);
  startTour();
}

export function useTourOpen(): boolean {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return open;
}
